import { CreditCard, GitCompare, TrendingUp, Receipt, PiggyBank, Lightbulb } from "lucide-react";
import type { ArticleContent, ArticleTile } from "./types";
import { Hero, Section, Steps, Bullets, Tip } from "./renderHelpers";

const CATEGORY = "Payments";

export const tiles: ArticleTile[] = [
  { slug: "stripe-connect-setup", title: "Getting Started with Stripe Connect", category: CATEGORY, description: "Connect Stripe to take card payments", icon: CreditCard },
  { slug: "mollie-vs-stripe", title: "Choosing Between Mollie and Stripe", category: CATEGORY, description: "Pick the right payment provider for your PTA", icon: GitCompare },
  { slug: "recording-income-expenses", title: "Recording Income & Expenses", category: CATEGORY, description: "Track every penny in and out", icon: TrendingUp },
  { slug: "expense-claims", title: "Submitting & Approving Expense Claims", category: CATEGORY, description: "Reimburse members for out-of-pocket spend", icon: Receipt },
  { slug: "available-funds-explained", title: "Available Funds, Ring-Fencing & Reserves", category: CATEGORY, description: "Understand what your PTA can actually spend", icon: PiggyBank },
];

export const articles: Record<string, ArticleContent> = {
  "stripe-connect-setup": {
    title: "Getting Started with Stripe Connect",
    category: CATEGORY,
    readTime: "5 min read",
    content: (
      <div className="space-y-6">
        <Hero>Stripe Connect lets your PTA accept card, Apple Pay and Google Pay payments. Onboarding takes about 10 minutes through Stripe Express.</Hero>
        <Section icon={CreditCard} title="Connect your account">
          <Steps items={[
            <>Go to <strong>Settings → Payments</strong>.</>,
            <>Click <strong>Connect with Stripe</strong>.</>,
            <>You'll be redirected to Stripe Express to enter your charity/PTA details and bank account.</>,
            <>Once approved, return to PTA Hub — payments are enabled automatically.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="What you can take payments for">
          <Bullets items={[
            "Event tickets and stall payments.",
            "Marketplace listings and parent credits.",
            "Loan repayments and game entries.",
            "Sponsorship packages.",
          ]} />
        </Section>
        <Tip>You can use either Stripe or Mollie, but not both — they're mutually exclusive.</Tip>
      </div>
    ),
  },
  "mollie-vs-stripe": {
    title: "Choosing Between Mollie and Stripe",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Both providers are great. The right one depends on your PTA's banking setup and which payment methods your parents prefer.</Hero>
        <Section icon={CreditCard} title="Choose Stripe if">
          <Bullets items={[
            "You want the fastest onboarding (Express, ~10 minutes).",
            "Most parents pay by Apple Pay, Google Pay or card.",
            "You want strong recurring/subscription support in future.",
          ]} />
        </Section>
        <Section icon={CreditCard} title="Choose Mollie if">
          <Bullets items={[
            "You want iDEAL or other European payment methods.",
            "You're already set up as a Mollie charity account (£50 setup fee applies).",
            "You prefer Mollie's fee structure for low-volume payments.",
          ]} />
        </Section>
        <Tip>You can only have one provider active at a time — switching means disconnecting one before connecting the other.</Tip>
      </div>
    ),
  },
  "recording-income-expenses": {
    title: "Recording Income & Expenses",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Keep your books accurate by logging every transaction in the Treasurer dashboard. Online payments are recorded automatically.</Hero>
        <Section icon={TrendingUp} title="Add a manual income entry">
          <Steps items={[
            <>Open the <strong>Treasurer Dashboard</strong>.</>,
            <>Click <strong>Add Income</strong>.</>,
            <>Enter the amount, date, source and category.</>,
            <>Optionally link it to a specific event for reporting.</>,
            <>Save — it appears in your running balance instantly.</>,
          ]} />
        </Section>
        <Section icon={Receipt} title="Auto-recorded income">
          <Bullets items={[
            "Event ticket sales (Stripe or Mollie).",
            "Stall application fees.",
            "Parent credit purchases.",
            "Loan repayments and game entries.",
          ]} />
        </Section>
        <Tip>Expenses follow the same flow — use <strong>Add Expense</strong> and attach a receipt where possible.</Tip>
      </div>
    ),
  },
  "expense-claims": {
    title: "Submitting & Approving Expense Claims",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Members can submit out-of-pocket expenses with a receipt; treasurers and trustees approve and reimburse them.</Hero>
        <Section icon={Receipt} title="Submit a claim">
          <Steps items={[
            <>From the dashboard, click <strong>Submit Expense Claim</strong>.</>,
            <>Enter amount, description and the event it relates to.</>,
            <>Upload a photo or scan of the receipt.</>,
            <>Submit — your treasurer is notified by email.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Approve a claim (Treasurer/Trustee)">
          <Bullets items={[
            "Open the Treasurer dashboard → Expense Claims tab.",
            "Review the receipt and details.",
            "Approve to record the expense and mark for reimbursement, or reject with a reason.",
          ]} />
        </Section>
        <Tip>Claim visibility is restricted to the claimant, treasurer and trustee for privacy.</Tip>
      </div>
    ),
  },
  "available-funds-explained": {
    title: "Available Funds, Ring-Fencing & Reserves",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Available Funds is what your PTA can actually spend right now — after setting aside ring-fenced money and reserves.</Hero>
        <Section icon={PiggyBank} title="The formula">
          <div className="bg-muted rounded-lg p-4 font-mono text-sm">
            Available Funds = (Income − Expenses) − Ring Fenced − Reserve Amount
          </div>
        </Section>
        <Section icon={Lightbulb} title="What each piece means">
          <Bullets items={[
            "Income − Expenses: your raw bank balance from PTA Hub records.",
            "Ring Fenced: money committed to a specific cause (e.g. playground fund).",
            "Reserve Amount: a safety buffer set in Settings → Treasurer.",
          ]} />
        </Section>
        <Tip>Set ring-fenced totals against specific fundraising goals so the dashboard shows progress automatically.</Tip>
      </div>
    ),
  },
};
